/**
 * Money: one amount, one currency, never mixed and never invented.
 *
 * WHY THIS EXISTS. Every price in the product passed through code that threw the
 * currency away and then re-labelled the number with a dollar sign. Five
 * separate defects, all reachable by typing a price into an activity:
 *
 *   1. SUMS ACROSS CURRENCIES. A day total was
 *      `Number(budget.replace(/[^0-9.]/g, ''))` reduced over the day's stops, so
 *      a €50 lunch plus a $50 museum read "$100" — a number that is not true in
 *      any currency. The regex also stripped `-`, so a refund became a charge.
 *   2. RAW FLOATS ON SCREEN. That total was rendered with no formatting at all:
 *      19.99 + 5.99 + 3.99 printed as "$29.969999999999995".
 *   3. INVENTED SYMBOLS. The phone's formatter knew CAD and EUR and fell through
 *      to `$` for everything else, so a ¥150,000 budget displayed as "$150,000"
 *      — about 150x wrong — while the web showed the same trip correctly. The
 *      web's own table mapped JPY and CNY both to "¥".
 *   4. THE WRONG CURRENCY STORED. The activity form guessed the currency from
 *      whichever symbol it found in a free-text field and defaulted to USD, so
 *      "¥5000" was persisted as `{amount: 5000, currency: 'USD'}`. The fix for
 *      that one is a currency selector, not a better guess — the UI must ASK.
 *   5. One formatter rounded, the other did not, so the same trip's budget
 *      appeared as "C$222" here and "C$222.222" there.
 *
 * THE RULES. Amounts in different currencies are never added — a mixed day
 * reports each currency's own subtotal ("€50 + $50"), because that is the only
 * true answer without a rate and an as-of date, neither of which the client has.
 * Formatting goes through `Intl`, which knows each currency's symbol and its
 * number of minor units (JPY and KRW have none), so no hand-kept symbol table
 * can drift. And an amount whose currency is missing or malformed is shown as a
 * bare number: inventing a symbol for it is defect 3 all over again.
 *
 * Import-free and structural so the mobile copy can be byte-identical — see the
 * pinning test beside this file, and `sharedChat.ts` for the same arrangement.
 */

export interface Money {
  amount: number;
  currency: string;
}

export interface MoneyFormat {
  /**
   * Whole units only, no minor units: for trip budgets and per-day averages,
   * which are round figures someone chose. Activity costs and day totals are
   * real prices, so they keep their cents by default.
   */
  whole?: boolean;
}

/**
 * What the currency pickers offer, most-used first.
 *
 * A list, not a free-text field: the field it replaces inferred the currency
 * from a typed symbol, which is how `¥5000` ended up stored as USD. Any ISO 4217
 * code formats correctly whether or not it is listed here — this is the UI's
 * shortlist, not the set of currencies the code understands.
 */
export const CURRENCY_CHOICES = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY',
  'INR', 'MXN', 'BRL', 'SEK', 'NOK', 'DKK', 'NZD', 'KRW',
  'SGD', 'HKD', 'THB', 'TRY', 'PLN', 'CZK', 'ZAR', 'AED',
];

/** `Intl` accepts exactly three ASCII letters and throws a RangeError otherwise. */
function isCurrencyCode(currency: unknown): boolean {
  return typeof currency === 'string' && /^[A-Za-z]{3}$/.test(currency);
}

/**
 * An amount with no currency we can name.
 *
 * Deliberately symbol-less. The alternative — picking a default — is exactly the
 * bug this module exists to remove, and a bare "50" tells the reader the truth:
 * we know how much, not of what.
 */
function formatUnknownCurrency(amount: number, whole: boolean): string {
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: whole ? 0 : 2,
  });
}

/**
 * "$29.97" · "¥150,000" · "CA$1,234.50" · "CN¥1,234.50".
 *
 * `Intl` supplies the symbol and the right number of minor units per currency,
 * which also disposes of the float noise: it rounds to those units, so
 * 29.969999999999995 formats as "$29.97" rather than being rounded away to "$30"
 * the way a blanket `Math.round` did.
 */
export function formatMoney(money: Money | null | undefined, format: MoneyFormat = {}): string {
  const amount = Number(money?.amount);
  if (!Number.isFinite(amount)) return '';

  const whole = format.whole === true;
  if (!isCurrencyCode(money?.currency)) return formatUnknownCurrency(amount, whole);

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (money as Money).currency,
      ...(whole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}),
    }).format(amount);
  } catch {
    // Reachable where Intl is built without currency data. Falling back to the
    // ISO code keeps the amount honest — "JPY 150,000" is readable and cannot
    // be mistaken for dollars.
    return `${(money as Money).currency.toUpperCase()} ${formatUnknownCurrency(amount, whole)}`;
  }
}

/**
 * One subtotal per currency — the only sum that is ever true.
 *
 * Zero and non-finite amounts are dropped so an itinerary of free stops totals
 * nothing rather than "$0 + €0". Ordered by size, then by code, so the same
 * stops always read in the same order.
 */
export function sumByCurrency(items: (Money | null | undefined)[]): Money[] {
  const totals = new Map<string, number>();

  for (const item of items) {
    const amount = Number(item?.amount);
    if (!Number.isFinite(amount) || amount === 0) continue;
    // Anything we cannot name shares one bucket. It must not join a named
    // currency's subtotal, which would be defect 1 by another route.
    const currency = isCurrencyCode(item?.currency) ? (item as Money).currency.toUpperCase() : '';
    totals.set(currency, (totals.get(currency) || 0) + amount);
  }

  return [...totals.entries()]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => {
      // An unnameable amount reads last: "$50 + 50" says which part we are sure
      // of, where "50 + $50" looks like a formatting slip.
      if (!a.currency !== !b.currency) return a.currency ? -1 : 1;
      return Math.abs(b.amount) - Math.abs(a.amount) || a.currency.localeCompare(b.currency);
    });
}

/**
 * "€50 + $50" — every currency present, each with its own true total.
 *
 * Joined rather than converted: a rate belongs to a moment and the client has
 * neither the rate nor the authority to pick one, so showing both is the honest
 * answer. Empty when nothing was spent, which callers render as "Free" or omit.
 */
export function formatMoneyTotals(totals: Money[], format: MoneyFormat = {}): string {
  return totals.map((total) => formatMoney(total, format)).join(' + ');
}

/** `sumByCurrency` then `formatMoneyTotals` — what every spend line needs. */
export function formatSpend(
  items: (Money | null | undefined)[],
  format: MoneyFormat = {},
): string {
  return formatMoneyTotals(sumByCurrency(items), format);
}

/**
 * A typed amount, or null when the field holds nothing usable.
 *
 * Parses the NUMBER only. The currency comes from the picker beside it, never
 * from the text — reading it out of a typed symbol is what stored yen as
 * dollars. A leading minus is kept: a negative is a refund, and silently
 * flipping its sign (the old regex dropped the `-`) turned money back into
 * money spent.
 */
export function parseAmount(text: string | number | null | undefined): number | null {
  if (typeof text === 'number') return Number.isFinite(text) ? text : null;
  if (typeof text !== 'string') return null;

  const cleaned = text.trim().replace(/[^0-9.-]/g, '');
  if (!cleaned || !/^-?\d*\.?\d+$/.test(cleaned)) return null;

  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : null;
}

/**
 * The form's two fields as one value to store, or undefined for "no price".
 *
 * The single place a `Money` is built from user input, so the currency can only
 * ever be the one the person chose.
 */
export function moneyFromInput(
  amountText: string | number | null | undefined,
  currency: string | null | undefined,
): Money | undefined {
  const amount = parseAmount(amountText);
  if (amount === null || amount === 0) return undefined;
  return { amount, currency: isCurrencyCode(currency) ? currency!.toUpperCase() : 'USD' };
}
